import { useDispatch, useSelector } from "react-redux";

import {
  Box,
  ContextMenuButton,
  ContextMenuLink,
  Divider,
  Icon,
  Inline,
  Link,
  Stack,
  Text,
  TextHoverMenu
} from "@seneca/senekit";

import { linkToHealthResourcesArticle, linkToRegionModal } from "common/links";

import {
  getUserAccountType,
  getUserGivenName,
  getUserNickName
} from "seneca-common/features/user/state";
import { openWindowSecurely } from "seneca-common/utils/routing";

import DarkModeMenuButton from "features/dark-mode/components/DarkModeMenuButton";
import { useDarkModeGatingCriteria } from "features/dark-mode/hooks/useDarkModeGatingCriteria";
import {
  linkToDashboard,
  linkToDashboardFeedback,
  linkToDashboardInviteParentsModal,
  linkToStudentHelpSection
} from "features/dashboard/links";
import { linkToStudentClasses } from "features/dashboard/pages/classes/links";
import { linkToCoursesPage } from "features/dashboard/pages/courses-page/links";
import { linkToSettings } from "features/dashboard/pages/settings-page/links";
import { analyticsLogUserClickedActivateTutoringNavbar } from "features/tutoring/activation-journey/analytics";
import { setPlurallHelpCentreModalOpen } from "features/ui-state/actions";

export default function DesktopNavbar({
  sellPremiumToUser,
  sellUpgradeToUser,
  isTeacher,
  hasTutoringInactive,
  isPlurallApp,
  linkToWhatIsPremium,
  GetPremium,
  UpgradePremium,
  ActivateTutoring,
  linkToTutoringActivation,
  ...hoverMenuProps
}: any) {
  return (
    <Inline space="xxlarge">
      <Link exact navLink to={linkToDashboard()} tone="neutral">
        Home
      </Link>
      <Link navLink to={linkToCoursesPage()} tone="neutral">
        Courses
      </Link>
      <Link navLink to={linkToStudentClasses()} tone="neutral">
        Classes & assignments
      </Link>
      {!isTeacher && sellPremiumToUser && (
        <Link navLink to={linkToWhatIsPremium} tone="neutral">
          <GetPremium />
        </Link>
      )}
      {hasTutoringInactive && (
        <Link
          to={linkToTutoringActivation}
          onClick={analyticsLogUserClickedActivateTutoringNavbar}
          tone="neutral"
        >
          <ActivateTutoring />
        </Link>
      )}
      {!isTeacher && sellUpgradeToUser && (
        <Link navLink to={linkToWhatIsPremium} tone="neutral">
          <UpgradePremium />
        </Link>
      )}

      {/* @ts-ignore */}
      <DesktopHoverMenu {...hoverMenuProps} />
    </Inline>
  );
}

function DesktopHoverMenu({
  email,
  locale,
  signOut,
  showRegionLink,
  updateAppVersion,
  appVersionUpdateReady,
  appVersionIsUpdating,
  isPlurallApp
}: any) {
  const dispatch = useDispatch();

  const givenName = useSelector(getUserGivenName);
  const nickname = useSelector(getUserNickName);
  const role = useSelector(getUserAccountType);
  const name = role === "schoolteacher" && nickname ? nickname : givenName;

  const { isLoading: isDarkModeCriteriaLoading } = useDarkModeGatingCriteria();
  return (
    <TextHoverMenu position="bottomLeft" ariaLabel="More navigation options">
      {{
        label: (
          <Text dataNoTranslate size="medium" weight="bold">
            {name}
          </Text>
        ),
        options: (
          <Box width="200px" margin="large">
            <Stack space="large">
              {!isPlurallApp && (
                <>
                  <Box margin="large">
                    <Text dataNoTranslate size="small" tone="neutral" truncate>
                      {email}
                    </Text>
                  </Box>

                  <Divider direction="horizontal" />
                </>
              )}

              <Stack>
                <ContextMenuLink to={linkToSettings()}>
                  {{
                    icon: (
                      <Icon
                        iconName="cog"
                        size="medium"
                        accessibilityLabel={null}
                        tone="neutral"
                      />
                    ),
                    text: <Text>Settings</Text>
                  }}
                </ContextMenuLink>
                {!isDarkModeCriteriaLoading && <DarkModeMenuButton />}

                {!isPlurallApp && (
                  <ContextMenuLink to={linkToDashboardInviteParentsModal()}>
                    {{
                      icon: (
                        <Icon
                          iconName="group"
                          size="medium"
                          accessibilityLabel={null}
                          tone="neutral"
                        />
                      ),
                      text: <Text>Invite parent</Text>
                    }}
                  </ContextMenuLink>
                )}

                {showRegionLink && (
                  <ContextMenuLink to={linkToRegionModal()}>
                    {{
                      icon: (
                        <Icon
                          iconName="globe"
                          size="medium"
                          accessibilityLabel={null}
                          tone="neutral"
                        />
                      ),
                      text: <Text>Region</Text>
                    }}
                  </ContextMenuLink>
                )}

                {!isPlurallApp && (
                  <ContextMenuLink to={linkToDashboardFeedback()}>
                    {{
                      icon: (
                        <Icon
                          iconName="megaphone"
                          size="medium"
                          accessibilityLabel={null}
                          tone="neutral"
                        />
                      ),
                      text: <Text>Feedback</Text>
                    }}
                  </ContextMenuLink>
                )}

                <ContextMenuButton
                  onClick={() =>
                    isPlurallApp
                      ? dispatch(setPlurallHelpCentreModalOpen())
                      : openWindowSecurely(linkToStudentHelpSection(locale))
                  }
                >
                  {{
                    icon: (
                      <Icon
                        iconName="help"
                        size="medium"
                        accessibilityLabel={"Help"}
                        tone="neutral"
                      />
                    ),
                    text: <Text>Help centre</Text>
                  }}
                </ContextMenuButton>
                {/* TODO Look into if we will replace this with SOMOS equivalent */}
                {!isPlurallApp && (
                  <ContextMenuButton
                    onClick={() =>
                      openWindowSecurely(linkToHealthResourcesArticle(locale))
                    }
                  >
                    {{
                      icon: (
                        <Icon
                          iconName="heart"
                          size="medium"
                          accessibilityLabel={null}
                          tone="neutral"
                        />
                      ),
                      text: <Text>Mental wellbeing</Text>
                    }}
                  </ContextMenuButton>
                )}
              </Stack>

              <Divider direction="horizontal" />

              {appVersionUpdateReady && [
                <Stack>
                  <ContextMenuButton onClick={updateAppVersion}>
                    {{
                      icon: (
                        <Icon
                          iconName="reassign"
                          size="medium"
                          accessibilityLabel={null}
                          tone="neutral"
                        />
                      ),
                      text: (
                        <Text>
                          {appVersionIsUpdating ? (
                            <>Updating...</>
                          ) : (
                            <>Update Seneca</>
                          )}
                        </Text>
                      )
                    }}
                  </ContextMenuButton>
                </Stack>,
                <Divider direction="horizontal" />
              ]}

              <ContextMenuButton onClick={signOut}>
                {{
                  icon: (
                    <Icon
                      iconName="leave"
                      size="medium"
                      accessibilityLabel={null}
                      tone="neutral"
                    />
                  ),
                  text: <Text>Logout</Text>
                }}
              </ContextMenuButton>
            </Stack>
          </Box>
        )
      }}
    </TextHoverMenu>
  );
}
